<template>
  <div v-if="!hasOrganizerAccess && !loadingProfile">
    <no-data-message v-if="nonOrganizerText">
      <v-icon slot="icon">flag</v-icon>
      <template slot="message">
        {{ nonOrganizerText }}
      </template>
    </no-data-message>
  </div>
  <div v-else-if="loadingProfile" class="loader-container">
    <loader></loader>
  </div>
  <v-app v-else class="white" v-scroll="onScroll">
    <div class="white pb-2 main">
      <div class="main">
        <div class="header-view">
          <v-layout row wrap class="pb-3">
            <v-flex xs12 sm5>
              <v-layout row wrap>
                <v-btn flat icon @click="toggleSearchProject=!toggleSearchProject"><i class="search-icon pr-4"></i></v-btn>
                <v-text-field
                  v-if="toggleSearchProject"
                  type="text"
                  placeholder="search"
                  v-model="searchProjectValue"
                  class="pl-2 pt-2"
                  clearable
                >
                </v-text-field>
              </v-layout>
            </v-flex>
          </v-layout>
        </div>

        <div v-if="!dataLoading && !loadingProfile && noProjects" class="centered">
          <no-data-message>
            <v-icon slot="icon">flag</v-icon>
            <template slot="message">
              There are no projects to show
            </template>
            <template slot="sub-message">
              You can start by creating a new project
            </template>
            <template slot="button">
              <button @click="addNewProject">
                Create Project
              </button>
            </template>
          </no-data-message>
        </div>
        <v-container v-else fluid class="pa-0">

          <div class="project-view" v-for="(project, i) in organizerProjects" :key="project.project_id">
            <v-layout row wrap class="list-elements">
              <v-flex xs12 sm4 style="cursor: pointer;" class="py-2">
                <span class="project-name" @click="toProjectDetails(project.project_id)" v-if="project.project_id">
                  {{ project.project_name || project.project_ref }}
                </span>
              </v-flex>

              <v-flex xs12 sm2 style="padding-top: 5px;">
                <span class="status white--text" :class="getStatusClass(project.project_status)">
                  <span class="status__text">
                    {{ getStatusText(project.project_status) }}
                  </span>
                </span>
              </v-flex>

              <v-flex xs12 sm4 class="py-2 project__metrics">
                <span>
                  <span class="sub-headers">{{ project.project_tasks }}</span>
                  <v-icon style="opacity: 0.65" class="headers-icon small-icon tasks-icon" title="Effort"></v-icon>
                </span>

                <span>
                  <span class="sub-headers">{{ project.project_followers }}</span>
                  <v-icon class="headers-icon small-icon profile-grey-icon" title="Followers"></v-icon>
                </span>

                <span>
                  <span class="sub-headers">{{ project.project_contributors }}</span>
                  <v-icon class="headers-icon small-icon contributors-grey-icon" title="Contributors"></v-icon>
                </span>

                <span>
                  <span class="sub-headers">{{ project.project_completions }}</span>
                  <v-icon class="headers-icon small-icon completions-grey-icon" title="Completions"></v-icon>
                </span>
              </v-flex>

              <v-flex xs12 sm2 class="py-2 project__actions">
                <v-tooltip top>
                  <v-btn slot="activator" icon class="action-icons" :to="{ name: 'ProjectEdit', params: {id: project['project_id']} }">
                    <v-icon>edit</v-icon>
                  </v-btn>
                  <span>Edit</span>
                </v-tooltip>


                <v-tooltip top>
                  <v-btn slot="activator" icon class="action-icons" @click="copyProjectDetailsLink(project.project_id)">
                    <v-icon>link</v-icon>
                  </v-btn>
                  <span>Project details link</span>
                </v-tooltip>

                <v-tooltip top>
                  <v-btn slot="activator" icon class="action-icons" :to="{ name: 'OrganizerTasks', params: {id: project['project_id']} }">
                    <v-icon>extension</v-icon>
                  </v-btn>
                  <span>Effort</span>
                </v-tooltip>

                <v-tooltip top>
                  <v-btn slot="activator" icon class="action-icons" :to="{ name: 'ProjectChat', params: {id: project['project_id']} }">
                    <v-icon>speaker_notes</v-icon>
                  </v-btn>
                  <span>Discuss</span>
                </v-tooltip>

                <v-tooltip top>
                  <v-btn slot="activator" icon class="action-icons" :to="{ name: 'OrganizerCompletions', query: {project_id: project.project_id}}">
                    <v-icon>list</v-icon>
                  </v-btn>
                  <span>Completions</span>
                </v-tooltip>
              </v-flex>

            </v-layout>
          </div>
        </v-container>
        <div v-if="dataLoading" class="loader-container">
          <loader></loader>
        </div>

      </div>
      <div v-if="!dataLoading && !noProjects">
        <div class="add-btn">
          <v-btn large class="fixed-primary-button white--text" @click="addNewProject()">
            Add Project
          </v-btn>
        </div>
      </div>
    </div>
  </v-app>
</template>

<script>
import { get } from 'lodash';
import organizer from '@/mixins/organizer';
import clipboard from '@/mixins/clipboard';
import Loader from '@/components/Loader';

import { getNewProjectId } from '@/api';

export default {
  mixins: [organizer, clipboard],
  components: {
    Loader,
  },
  data() {
    return {
      lastScroll: 0,
      toggleSearchProject: false,
      searchProjectValue: '',
    };
  },
  methods: {
    copyProjectDetailsLink(projectId) {
      const routeName = this.$router.resolve({
        name: 'ProjectOverview',
        params: { id: projectId },
      });
      const origin = window.location.origin;
      this.clipboard(`${origin}/${routeName.href}`);
      this.$store.dispatch('auth/setFlashMessage', { message: 'Project detail link has been copied to clipboard.', type: 'success' });
    },
    onScroll() {
      const scroll = this.$scrollPercent();
      const scrollingUp = this.lastScroll > scroll;
      this.lastScroll = scroll;

      if (scroll < 0.90 || scrollingUp) return null;

      this.$store.dispatch('organizer/getOrganizerProjects');
      return null;
    },
    getStatusText(value) {
      let statusText = '';
      switch (value) {
        case '0':
          statusText = 'Pending';
          break;
        case '1':
          statusText = 'Live';
          break;
        case '2':
          statusText = 'In Progress';
          break;
        case '3':
          statusText = 'Completed';
          break;
        case '4':
          statusText = 'Archived';
          break;
        default:
          break;
      }
      return statusText;
    },
    getStatusClass(value) {
      let statusClass = '';
      switch (value) {
        case '0':
          statusClass = 'pending';
          break;
        case '1':
          statusClass = 'live';
          break;
        case '2':
          statusClass = 'inprogress';
          break;
        case '3':
          statusClass = 'completed';
          break;
        case '4':
          statusClass = 'archived';
          break;
        default:
          break;
      }
      return statusClass;
    },
    toProjectDetails(projectId) {
      this.$router.push({ name: 'ProjectOverview', params: { id: projectId } });
    },
    toProjectChat(projectId) {
      this.$router.push({ name: 'ProjectChat', params: { id: projectId } });
    },
    searchProjects(query) {
      this.$store.dispatch('organizer/searchOrganizerProjects', query);
    },
    addNewProject() {
      const userToken = this.$store.getters['auth/userToken'];
      getNewProjectId(userToken)
        .then(({ data }) => {
          const projectId = get(data, '[1].data.details.project_id');
          this.$router.push({ name: 'ProjectEdit', params: { id: projectId } });
        });
    },
  },
  computed: {
    noProjects() {
      return this.organizerProjects.length === 0;
    },
    dataLoading() {
      return this.$store.getters['organizer/projectsLoading'];
    },
    organizerProjects() {
      return this.$store.getters['organizer/organizerProjects'];
    },
  },
  watch: {
    searchProjectValue(val) {
      this.searchProjects(val);
    },
  },
  mounted() {
    this.$store.dispatch('shared/setDataLoading', true);
    this.$store.dispatch('organizer/projectReset');
    this.$store.dispatch('organizer/getOrganizerProjects');
    this.$store.dispatch('organizer/getOrganizerProjectsBasicDetails');
  },
};
</script>

<style lang="scss" scoped>
@import "~styles/shared.scss";

.mt-6 {
  margin-top: 6px;
}

.main {
  height: 100%;
}

.project-view:nth-child(odd) {
  background: $filter-background;
}

.headers {
  font-size: 13px;
  color: $header;
}

.loader-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.sub-headers {
  font-size: 12px;
  color: $placeholder;
}

.project-name {
  font-size: 16px;
  color: $blue;
}

.fixed-primary-button {
  background: $primary;
  font-family: MuseoSans-900;
  text-align: center;
  border-radius: 24px;
  padding: 1px;
}

.header-view {
  border-bottom: 1px solid #e5e5e5;
}


//edit class is to change css of buttons used in the list
.project-actions {
  span {
    color: $blue;
    font-size: 13px;
  }
}

.add-btn {
  position: fixed;
  bottom: 5px;
  left: calc(50% - 70px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 5;
  margin-bottom: 20px;
}


@media only screen and (max-width:600px) {
  .add-btn {
    margin-bottom: 70px;
  }
}

@media only screen and (min-width:1600px) {
  .add-btn {
    left: calc(50vw + 100px);
  }
}

.list-elements {
  padding-top: 10px;
  padding-bottom: 10px;
  padding-left: 16px;
  line-height: 2;
  padding-right: 16px;
  .btn {
    min-width: auto;
  }
}

.search-input:focus {
  outline: none;
  border-bottom: 1px solid $blue;
  width: 80%;
  font-size: 16px;
}

@media only screen and (max-width:1600px) {
  .headers {
    display: none;
  }

  .headers-icon {
    display: inherit;
    margin-left: 2px;
  }
}

@media only screen and (min-width: 1600px) {
  .headers-icon {
    display: none;
  }
}

@media only screen  and (max-width: 600px){
  .headers-icon {
    padding-top:24px;
  }
}

.action-icons.btn.btn--icon {
  color: #7e7e7e;
}

.project__metrics > span {
  display: inline-block;
}

.project__metrics > span::after {
  content: '\00B7';
  color: $placeholder;
  font-weight: bold;
  margin: 0 20px;
}

.project__metrics > span:last-child::after {
  display: none;
}

.status {
  min-width: 50px;
  font-size: 13px;
  border-radius:3px;
  padding: 3px;
  color: white;
  margin-top: 10px;
}

.pending {
  background: $pending;
}

.live {
  background: $live;
}

.completed {
  background: $completed;
}

.inprogress {
  background: $inprogress;
}

.archived {
  background: $filter-background;
}

.status__text {
  display: inline-block;
  min-width: 100px;
  text-align: center;
}

.project__actions {
  display: flex;
  justify-content: flex-end;
}

.half-circle-spinner {
  width: 30px;
  height: 30px;
}
</style>



// WEBPACK FOOTER //
// src/components/OrganizerProjects.vue